import { OutputData } from '@editorjs/editorjs';
import { useEffect, useMemo, useState } from 'react';
import { IoClose } from 'react-icons/io5';
import { ActionIcon, Button, Input, Modal, Select, Text } from 'rizzui';
import { toast } from 'sonner';
import useCorporations from '../../hooks/api-calls/useCorporations';
import usePaymentMethods from '../../hooks/api-calls/expirations/usePaymentMethods';
import useProducts from '../../hooks/api-calls/useProducts';
import { ClientService } from '../../services/client/clientService';
import { ContractService } from '../../services/contract/contractService';
import { ExpirationService } from '../../services/expiration/expirationService';
import { UserService } from '../../services/user/userService';
import { buildBrandedEmailPreviewTemplate } from '../../utils/emailTemplatePreview';
import { convertEditorJSONToHTML, convertHTMLToEditorJSON, isValidHTML } from '../../utils/editorConversions';
import ContractTemplateWYSIWYGEditor from '../contract-templates/ContractTemplateWYSIWYGEditor';
import CustomSelect from '../forms/CustomSelect';
import { useTypeOptions } from './EmailTemplateList';
import './emailTemplateStyles.css';

interface EmailTemplateEditorProps {
  template?: any; // Puede ser JSON de Editor.js o HTML
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, template: OutputData | string, usageType: string, organisingCorporationIds: string[], paymentMethodId: string | null, productIds: string[]) => void;
}

const EmailTemplateEditor: React.FC<EmailTemplateEditorProps> = ({
  template,
  isOpen,
  onClose,
  onSave
}) => {

  const [templateTitle, setTemplateTitle] = useState<string>('');
  const [usageType, setUsageType] = useState<string>('');
  const [organisingCorporationIds, setOrganisingCorporationIds] = useState<string[]>([]);
  const [productIds, setProductIds] = useState<string[]>([]);
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
  const [templateVariables, setTemplateVariables] = useState<any[]>([]);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [templateContentForPreview, setTemplateContentForPreview] = useState<string>('');
  const [previewEntityOptions, setPreviewEntityOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [selectedPreviewEntityId, setSelectedPreviewEntityId] = useState<string | null>(null);
  const [previewRenderedSubject, setPreviewRenderedSubject] = useState<string>('');
  const [previewRenderedContent, setPreviewRenderedContent] = useState<string>('');
  const [isPreviewRendering, setIsPreviewRendering] = useState(false);
  const { fetchCorporations, getCorporationsList } = useCorporations({ corporation_function: 'organising' });
  const { fetchPaymentMethods, getPaymentMethodsList } = usePaymentMethods();
  const { fetchProducts, getProductsList } = useProducts();

  const resolveTemplateContentForPreview = (templateContent?: any): string => {
    if (!templateContent) {
      return '';
    }

    if (typeof templateContent === 'string') {
      const trimmedContent = templateContent.trim();

      if ((trimmedContent.startsWith('{') || trimmedContent.startsWith('['))) {
        try {
          const parsed = JSON.parse(trimmedContent);
          if (parsed && typeof parsed === 'object' && Array.isArray((parsed as any).blocks)) {
            return convertEditorJSONToHTML(parsed as any);
          }
        } catch (_) {
          // noop
        }
      }

      return trimmedContent;
    }

    try {
      return convertEditorJSONToHTML(templateContent);
    } catch (_) {
      return '';
    }
  };

  useEffect(() => {
    if (!template) {
      setTemplateTitle('');
      setUsageType('');
      setTemplateContentForPreview('');
      return;
    }

    setTemplateTitle(template.name || '');
    setUsageType(template.useType || '');
    setOrganisingCorporationIds(
      Array.isArray(template.organisingCorporations)
        ? template.organisingCorporations
          .map((organisingCorporation: any) => organisingCorporation?.id)
          .filter((id: any): id is string => Boolean(id))
        : []
    );
    setProductIds(
      Array.isArray(template.products)
        ? template.products
          .map((product: any) => product?.id)
          .filter((id: any): id is string => Boolean(id))
        : []
    );
    setPaymentMethodId(template.paymentMethod?.id || null);
    setTemplateContentForPreview(resolveTemplateContentForPreview(template.template));
  }, [template]);

  useEffect(() => {
    if (!isOpen) {
      setTemplateTitle('');
      setUsageType('');
      setOrganisingCorporationIds([]);
      setProductIds([]);
      setPaymentMethodId(null);
      setTemplateVariables([]);
      setIsPreviewModalOpen(false);
      setTemplateContentForPreview('');
      setPreviewEntityOptions([]);
      setSelectedPreviewEntityId(null);
      setPreviewRenderedSubject('');
      setPreviewRenderedContent('');
      setIsPreviewRendering(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    fetchCorporations({ corporation_function: 'organising' });
    fetchPaymentMethods();
    fetchProducts();
  }, [isOpen]);

  useEffect(() => {
    if (!['contract', 'expiration'].includes(usageType) && paymentMethodId) {
      setPaymentMethodId(null);
    }
  }, [usageType, paymentMethodId]);

  useEffect(() => {
    const fetchTemplateVariables = async () => {
      if (!isOpen || !usageType) {
        setTemplateVariables([]);
        return;
      }

      try {
        const response = await (new UserService()).listEmailTemplateVariables(usageType);
        const responseData = response.getResponseData()?.data;
        setTemplateVariables(Array.isArray(responseData) ? responseData : []);
      } catch (error) {
        console.error('Error al cargar variables de plantilla de email:', error);
        setTemplateVariables([]);
        toast.error('No se pudieron cargar las variables para este tipo de plantilla');
      }
    };

    fetchTemplateVariables();
  }, [isOpen, usageType]);

  // Memoizar initialData para evitar re-renders del editor
  // Maneja tanto JSON como HTML del template
  const memoizedInitialData = useMemo(() => {
    if (!template) return undefined;

    const templateString = template.template as unknown as string;

    try {
      // Intenta parsear como JSON primero (formato legacy)
      return JSON.parse(templateString);
    } catch {
      // Si falla, asume que es HTML y convierte a JSON
      if (isValidHTML(templateString)) {
        return convertHTMLToEditorJSON(templateString);
      }
      return undefined;
    }
  }, [template]);

  const previewEmailHtml = useMemo(
    () => buildBrandedEmailPreviewTemplate(
      (previewRenderedSubject || templateTitle?.trim()) || 'Comunicación Attiva',
      previewRenderedContent || templateContentForPreview
    ),
    [templateTitle, templateContentForPreview, previewRenderedSubject, previewRenderedContent]
  );

  useEffect(() => {
    const fetchPreviewEntities = async () => {
      if (!isPreviewModalOpen || !usageType) {
        setPreviewEntityOptions([]);
        setSelectedPreviewEntityId(null);
        return;
      }

      try {
        let options: Array<{ value: string; label: string }> = [];

        if (usageType === 'contract') {
          const response = await (await (new ContractService()).listContracts({
            page: 1,
            limit: 200,
            filter_filters: { is_draft: false },
            filter_order: [{ field: 'created_at', order: 'desc' }]
          })).getResponseData();

          const contracts = response?.data?.data || [];
          options = contracts.map((contract: any) => ({
            value: contract.id,
            label: contract.clientCompanyName ? `${contract.code} - ${contract.clientCompanyName}` : contract.code
          }));
        } else if (usageType === 'client') {
          const response = await (await (new ClientService()).listClients({
            page: 1,
            limit: 200,
            filter_order: [{ field: 'company_name', order: 'asc' }]
          })).getResponseData();

          const clients = response?.data?.data || [];
          options = clients.map((client: any) => ({
            value: client.id,
            label: `${client.companyName} (${client.cifNif})`
          }));
        } else if (usageType === 'expiration') {
          const response = await (await (new ExpirationService()).listExpirations({
            page: 1,
            limit: 200,
            filter_order: [{ field: 'created_at', order: 'desc' }]
          })).getResponseData();

          const expirations = response?.data?.data || [];
          options = expirations.map((expiration: any) => ({
            value: expiration.id,
            label: expiration.contract?.code
              ? `${expiration.contract.code} - ${expiration.id}`
              : expiration.id
          }));
        }

        setPreviewEntityOptions(options);
        setSelectedPreviewEntityId((current) => {
          if (current && options.some((option) => option.value === current)) {
            return current;
          }
          return options[0]?.value || null;
        });
      } catch (error) {
        console.error('Error loading entities for email template preview:', error);
        setPreviewEntityOptions([]);
        setSelectedPreviewEntityId(null);
      }
    };

    fetchPreviewEntities();
  }, [isPreviewModalOpen, usageType]);

  useEffect(() => {
    const renderPreviewWithRealData = async () => {
      if (!isPreviewModalOpen || !usageType || !selectedPreviewEntityId || !templateContentForPreview) {
        setPreviewRenderedSubject('');
        setPreviewRenderedContent('');
        return;
      }

      setIsPreviewRendering(true);
      try {
        const payload: any = {
          useType: usageType,
          subject: templateTitle,
          templateContent: templateContentForPreview
        };

        if (usageType === 'contract') {
          payload.contractId = selectedPreviewEntityId;
        } else if (usageType === 'client') {
          payload.clientId = selectedPreviewEntityId;
        } else if (usageType === 'expiration') {
          payload.expirationId = selectedPreviewEntityId;
        }

        const response = await (await (new UserService()).previewEmailTemplate(payload)).getResponseData();
        if (response?.success) {
          setPreviewRenderedSubject(response?.data?.subject || '');
          setPreviewRenderedContent(response?.data?.content || '');
        } else {
          setPreviewRenderedSubject('');
          setPreviewRenderedContent('');
        }
      } catch (error) {
        console.error('Error rendering email template preview with real data:', error);
        setPreviewRenderedSubject('');
        setPreviewRenderedContent('');
      } finally {
        setIsPreviewRendering(false);
      }
    };

    renderPreviewWithRealData();
  }, [isPreviewModalOpen, usageType, selectedPreviewEntityId, templateTitle, templateContentForPreview]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl" className='min-h-[600px]'>
        <div className="p-6">
          <ContractTemplateWYSIWYGEditor
            type='email'
            title={template?.name ? 'Editar Plantilla' : 'Nueva Plantilla'}
            initialData={memoizedInitialData}
            onChange={(editorData) => {
              try {
                setTemplateContentForPreview(convertEditorJSONToHTML(editorData));
              } catch (_) {
                setTemplateContentForPreview('');
              }
            }}
            onSave={(data) => onSave(templateTitle, data, usageType, organisingCorporationIds, paymentMethodId, productIds)}
            placeholder="Escriba aquí la plantilla..."
            disabledSaving={
              !templateTitle
              || !usageType
              || organisingCorporationIds.length === 0
            }
            variables={templateVariables}
          />

          <div className='grid grid-cols-3 mt-4 gap-4'>
            <Input
              placeholder="Título de la plantilla"
              value={templateTitle}
              onChange={(e) => setTemplateTitle(e.target.value)}
              className="md:col-span-2"
            />

            <Select
              placeholder="Tipo de uso"
              value={usageType ? { label: useTypeOptions.find(option => option.value === usageType)?.label, value: usageType } : null}
              onChange={(e: any) => setUsageType(e?.value)}
              options={useTypeOptions.filter(option => option.value !== '')}
            />

            {['contract', 'expiration'].includes(usageType) && (
              <Select
                placeholder="Método de pago"
                value={paymentMethodId ? getPaymentMethodsList().find((method: any) => method.value === paymentMethodId) || null : null}
                onChange={(e: any) => setPaymentMethodId(e?.value || null)}
                options={getPaymentMethodsList()}
              />
            )}

            <div className='col-span-3'>
              <CustomSelect
                id="productIds"
                label="Productos"
                isMulti
                isSearchable
                isClearable
                options={getProductsList()}
                value={getProductsList().filter((product: any) => productIds.includes(product.value))}
                onChange={(selectedOptions: any) => {
                  const ids = Array.isArray(selectedOptions)
                    ? selectedOptions
                      .map((option: any) => option?.value)
                      .filter((value: any): value is string => Boolean(value))
                    : [];

                  setProductIds(ids);
                }}
                placeholder="productos"
              />
            </div>

            <div className='col-span-3'>
              <CustomSelect
                id="organisingCorporationIds"
                label="Entidades organizadoras"
                required
                isMulti
                isSearchable
                isClearable
                options={getCorporationsList()}
                value={getCorporationsList().filter((corporation: any) => organisingCorporationIds.includes(corporation.value))}
                onChange={(selectedOptions: any) => {
                  const ids = Array.isArray(selectedOptions)
                    ? selectedOptions
                      .map((option: any) => option?.value)
                      .filter((value: any): value is string => Boolean(value))
                    : [];

                  setOrganisingCorporationIds(ids);
                }}
                placeholder="entidades organizadoras"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsPreviewModalOpen(true)}
              disabled={!templateTitle && !templateContentForPreview}
            >
              Previsualizar email
            </Button>
            <Button variant="outline" color="danger" onClick={() => { onClose(); setTemplateTitle(''); setUsageType(''); setOrganisingCorporationIds([]); setProductIds([]); setPaymentMethodId(null); setTemplateVariables([]); }}>
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        size="xl"
      >
        <div className="m-auto px-5 py-5">
          <div className="mb-4 flex items-center justify-between">
            <Text className="text-lg font-semibold text-gray-900">
              Previsualización del email
            </Text>
            <ActionIcon
              size="sm"
              variant="text"
              onClick={() => setIsPreviewModalOpen(false)}
              className="p-0 text-gray-500 hover:!text-gray-900"
            >
              <IoClose className="h-[18px] w-[18px]" />
            </ActionIcon>
          </div>

          <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
            <Select
              placeholder="Entidad para previsualizar"
              value={selectedPreviewEntityId
                ? previewEntityOptions.find((option) => option.value === selectedPreviewEntityId) || null
                : null}
              onChange={(selected: any) => setSelectedPreviewEntityId(selected?.value ?? null)}
              options={previewEntityOptions}
            />
            {isPreviewRendering && (
              <Text className="text-sm text-gray-500">Renderizando datos reales...</Text>
            )}
          </div>

          <iframe
            title="Previsualización plantilla de email"
            sandbox=""
            srcDoc={previewEmailHtml}
            className="h-[70vh] w-full rounded-lg border border-gray-200 bg-white"
          />
        </div>
      </Modal>
    </>
  );
};

export default EmailTemplateEditor;
